import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, output, signal } from '@angular/core';
import { TranslocoModule } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';

/** Milliseconden per minuut; de slider werkt op minutenresolutie. */
const MINUTE_MS = 60_000;
/** Afspeeltempo: één stap per tick. */
const PLAY_TICK_MS = 700;

/**
 * Tijdslider boven de chart (spec momenten stap 4): schuift de weergavetijd
 * van de transit-overlay binnen een bereik. Twee modi:
 *  - `range`: een bewaard moment mét periode — het anker is de bereik-start.
 *  - `live`: de just-now-overlay op een gewone chart — het anker is het
 *    initiële just-now-moment; de ankerknop snapt daarnaar terug.
 * Dom presentatiecomponent: de pagina bezit de stand, de samples en de pin
 * van de app-weergavetijd; dit component rekent minuten ↔ tijdstip om en
 * verzorgt stap-, afspeel- en spring-gedrag.
 *
 * Call-discipline (bijgestuurd 2026-09-17): de slider werkt op het
 * automatische stapraster (`stepMinutes`), dus slepen produceert alleen een
 * nieuwe stand per gepasseerd rasterpunt — op een jarenrange per dag in
 * plaats van per minuut. Minuut-precisie loopt via de momentkiezer op het
 * label. Discrete acties (loslaten, stapknop, afspeeltick, momentkiezer)
 * emitten daarnaast `valueUtcCommitted`, zodat de eigenaar die stand meteen
 * kan doorrekenen.
 */
@Component({
  selector: 'hd-transit-time-slider',
  standalone: true,
  imports: [SharedModule, TranslocoModule],
  templateUrl: './hd-transit-time-slider.component.html',
  styleUrl: './hd-transit-time-slider.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdTransitTimeSliderComponent {
  readonly startUtc = input.required<string>();
  readonly endUtc = input.required<string>();
  readonly valueUtc = input.required<string>();
  readonly mode = input.required<'range' | 'live'>();
  /** true zolang de slider op het anker staat (bereik-start of het nu-moment). */
  readonly atAnchor = input(false);
  /** Er loopt een sample-berekening voor de huidige stand. */
  readonly busy = input(false);

  readonly valueUtcChange = output<string>();
  /** Discrete stand (loslaten, stap, afspeeltick, momentkiezer): meteen doorrekenen. */
  readonly valueUtcCommitted = output<string>();
  readonly anchorRequested = output<void>();

  private readonly destroyRef = inject(DestroyRef);

  protected readonly playing = signal(false);
  protected readonly pickerOpen = signal(false);
  private playTimer: ReturnType<typeof setInterval> | null = null;

  private readonly startMs = computed(() => Date.parse(this.startUtc()));
  private readonly endMs = computed(() => Date.parse(this.endUtc()));
  private readonly valueMs = computed(() => Date.parse(this.valueUtc()));

  /** Slider in minuten vanaf de bereik-start. */
  protected readonly totalMinutes = computed(() =>
    Math.max(1, Math.round((this.endMs() - this.startMs()) / MINUTE_MS)));
  protected readonly valueMinutes = computed(() =>
    Math.min(this.totalMinutes(), Math.max(0, Math.round((this.valueMs() - this.startMs()) / MINUTE_MS))));

  /**
   * Automatische stapgrootte, tevens het sleep-raster: t/m 6 uur per 5
   * minuten, t/m 4 dagen per uur (dekt het live-venster van 2 dagen voor en
   * na), t/m 14 dagen per 6 uur, daarboven per dag.
   */
  protected readonly stepMinutes = computed(() => {
    const totalHours = this.totalMinutes() / 60;
    if (totalHours <= 6) return 5;
    if (totalHours <= 96) return 60;
    if (totalHours <= 14 * 24) return 6 * 60;
    return 24 * 60;
  });

  protected readonly atEnd = computed(() => this.valueMinutes() >= this.totalMinutes());
  protected readonly atStart = computed(() => this.valueMinutes() <= 0);

  /** Label van de huidige stand, lokaal geformatteerd (zelfde stijl als de moment-dialogen). */
  protected readonly valueLabel = computed(() =>
    new Intl.DateTimeFormat('nl-BE', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(this.valueMs())));

  /** Grenzen en huidige stand voor de momentkiezer (datetime-local werkt in lokale tijd). */
  protected readonly pickerMin = computed(() => toDatetimeLocal(this.startMs()));
  protected readonly pickerMax = computed(() => toDatetimeLocal(this.endMs()));
  protected readonly pickerValue = computed(() => toDatetimeLocal(this.valueMs()));

  constructor() {
    this.destroyRef.onDestroy(() => this.stopPlaying());
  }

  protected onSliderInput(rawValue: string): void {
    this.stopPlaying();
    const minutes = Number(rawValue);
    if (!Number.isFinite(minutes)) return;
    this.emitMinutes(this.snapToStep(minutes), { commit: false });
  }

  /** Loslaten van de slider: dezelfde (raster)stand, maar nu meteen doorrekenen. */
  protected onSliderCommit(): void {
    this.emitUtc(this.startMs() + this.valueMinutes() * MINUTE_MS, { commit: true, force: true });
  }

  protected stepBy(direction: -1 | 1): void {
    this.stopPlaying();
    this.emitMinutes(this.valueMinutes() + direction * this.stepMinutes(), { commit: true });
  }

  protected togglePlay(): void {
    if (this.playing()) {
      this.stopPlaying();
      return;
    }
    // Aan het einde herstart afspelen vanaf de bereik-start.
    if (this.atEnd()) this.emitMinutes(0, { commit: true });
    this.playing.set(true);
    this.playTimer = setInterval(() => {
      const next = this.valueMinutes() + this.stepMinutes();
      if (next >= this.totalMinutes()) {
        this.emitMinutes(this.totalMinutes(), { commit: true });
        this.stopPlaying();
        return;
      }
      this.emitMinutes(next, { commit: true });
    }, PLAY_TICK_MS);
  }

  protected togglePicker(): void {
    this.pickerOpen.update(open => !open);
  }

  /** Momentkiezer: exacte (minuut)stand binnen het bereik — het raster geldt hier bewust niet. */
  protected onPickerChange(rawValue: string): void {
    if (!rawValue) return;
    const picked = new Date(rawValue).getTime();
    if (!Number.isFinite(picked)) return;
    this.stopPlaying();
    this.pickerOpen.set(false);
    this.emitUtc(picked, { commit: true });
  }

  protected onAnchorClick(): void {
    this.stopPlaying();
    this.pickerOpen.set(false);
    this.anchorRequested.emit();
  }

  private snapToStep(minutes: number): number {
    const step = this.stepMinutes();
    return Math.round(minutes / step) * step;
  }

  private emitMinutes(minutes: number, options: { commit: boolean }): void {
    const clamped = Math.min(this.totalMinutes(), Math.max(0, minutes));
    // Zelfde rasterpunt = geen nieuwe stand (scheelt berekeningen tijdens het slepen).
    if (!options.commit && clamped === this.valueMinutes()) return;
    this.emitUtc(this.startMs() + clamped * MINUTE_MS, options);
  }

  private emitUtc(ms: number, options: { commit: boolean; force?: boolean }): void {
    const clampedMs = Math.min(this.endMs(), Math.max(this.startMs(), ms));
    const utcIso = new Date(clampedMs).toISOString();
    if (options.force !== true || clampedMs !== this.valueMs()) {
      this.valueUtcChange.emit(utcIso);
    }
    if (options.commit) {
      this.valueUtcCommitted.emit(utcIso);
    }
  }

  private stopPlaying(): void {
    if (this.playTimer != null) {
      clearInterval(this.playTimer);
      this.playTimer = null;
    }
    this.playing.set(false);
  }
}

/** Epoch-ms → "YYYY-MM-DDTHH:mm" in lokale tijd, het formaat van `input[type=datetime-local]`. */
function toDatetimeLocal(ms: number): string {
  const date = new Date(ms);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
